import { ShippingOption } from 'API';
import { SkeletonBox } from 'components/common/Skeleton';
import { useInvoicingDetail } from 'providers/InvoicingDetailModuleProvider';
import { FC } from 'react';
import { CarrierElement } from './CarrierElement/CarrierElement';
import { PackageTypeElement } from './PackageTypeElement/PackageTypeElement';
import { ShippingPreferenceColorElement } from './ShippingPreferenceColorElement/ShippingPreferenceColorElement';
import { ShippingPreferenceElement } from './ShippingPreferenceElement/ShippingPreferenceElement';
import { TrackingNumber } from './TrackingNumber/TrackingNumber';

/**
 * Component for displaying loading skeleton for shipping details.
 */
const ShippingDetailLoading = () => {
  return (
    <div className="flex flex-col gap-3 mt-2">
      <SkeletonBox className="w-full h-8" />
      <SkeletonBox className="w-full h-8" />
    </div>
  );
};

/**
 * Props interface for the ShippingDetailContent component.
 */
interface ShippingDetailContentProps {
  /**
   * Array of shipping options to display.
   */
  shippingOptions: ShippingOption[];
}

/**
 * Component for rendering the content of shipping details.
 * @param shippingOptions - Array of shipping options to display.
 * @returns JSX element representing the content of shipping details.
 */
const ShippingDetailContent: FC<ShippingDetailContentProps> = ({ shippingOptions }) => {
  if (!shippingOptions.length) return <div className="text-sm text-gray-500 mt-2">No shipping options found.</div>;
  return (
    <div className="flex flex-col">
      <TrackingNumber shippingOptions={shippingOptions} />
      <ShippingPreferenceElement />
      <PackageTypeElement shippingOptions={shippingOptions} />
      <CarrierElement />
      <ShippingPreferenceColorElement />
    </div>
  );
};

/**
 * Props interface for the ShippingDetail component.
 */
interface ShippingDetailProps {
  /**
   * Flag indicating whether the component is in loading state.
   */
  isLoading: boolean;
}

/**
 * Component for displaying shipping details.
 * @param isLoading - Flag indicating whether the component is in loading state.
 * @returns JSX element representing the shipping details.
 */
const ShippingDetail: FC<ShippingDetailProps> = ({ isLoading }) => {
  const { shippingOptions } = useInvoicingDetail();

  if (isLoading) return <ShippingDetailLoading />;
  return <ShippingDetailContent shippingOptions={shippingOptions} />;
};

export default ShippingDetail;
